/**
 * CentralHelpDeskDashboard.js
 *
 * Complaint-focused workflow management dashboard for Central Help Desk role
 *
 * Shows 7 sections:
 * 1. Quick Statistics - Complaint counts, device status, alerts
 * 2. Location Alerts - Critical issues requiring immediate action
 * 3. Unassigned Complaints Queue - Queue management with aging
 * 4. Technician Coverage & Availability - For assignment decisions
 * 5. Complaint SLA Performance - KPIs and metrics
 * 6. Device Summary - Device status and maintenance tracking
 * 7. Recent Activity - Workflow audit trail
 *
 * Scope: Location-scoped (their assigned location only)
 * Financial data: NOT visible
 *
 * Reference: DASHBOARD_MODULAR_ARCHITECTURE_PLAN.md Phase 2.6
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/hooks/useAuth';
import BaseDashboard from './BaseDashboard';
import DashboardLayout, { Section } from './DashboardLayout';
import EmptyState from '../widgets/Shared/EmptyState';
import { DeviceIDBadge, ComplaintIDBadge, StatusBadge, SeverityBadge, EmployeeIDBadge } from '../../../shared/components/common/Badges';
import ComplaintView from '../../complaints/components/ComplaintView';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { ComplaintPerformanceMetricsCards } from '../../analytics/components';
import StatsCard from '../../../shared/components/common/StatsCard';
import { COMPLAINTS_DEFAULT_LOOKBACK_DAYS } from '../../complaints/hooks';
import TeamWorkloadTable from '../widgets/Tables/TeamWorkloadTable';
import './Dashboards.css';

/**
 * CentralHelpDeskDashboard Component
 *
 * Displays complaint workflow management dashboard for help desk staff.
 *
 * Props:
 * - token: JWT authentication token (optional, uses auth context if not provided)
 * - user: Current user object (optional, uses auth context if not provided)
 * - onNavigateToDevicesWithFilter: Callback to navigate to devices view with filters
 * - onNavigateToComplaintsWithFilter: Callback to navigate to complaints view
 *
 * Automatically uses authenticated user's role from auth context
 */
const CentralHelpDeskDashboard = ({
  token: providedToken,
  user: providedUser,
  onNavigateToDevicesWithFilter,
  onNavigateToComplaintsWithFilter,
}) => {
  // Get authenticated user's role and token from context
  const { token: contextToken, user: contextUser } = useAuth();
  const token = providedToken || contextToken;
  const user = providedUser || contextUser;
  const [selectedComplaintForModal, setSelectedComplaintForModal] = useState(null);
  const [sortColumn, setSortColumn] = useState('severity'); // Default sort by severity (complaints)
  const [sortDirection, setSortDirection] = useState('desc'); // desc = critical first
  const [isCompactQuickNav, setIsCompactQuickNav] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const sync = () => setIsCompactQuickNav(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  // Handle smooth refresh after complaint update
  const handleComplaintUpdate = (refreshDashboard) => {
    // Close the modal first for smooth transition
    setSelectedComplaintForModal(null);

    // Then refresh the dashboard data after a small delay
    // This allows the modal to close smoothly without flickering
    setTimeout(() => {
      refreshDashboard();
    }, 300);
  };

  // Handle table header sort
  const handleSort = (column) => {
    // If clicking the same column, toggle direction; otherwise, set new column with desc
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Severity priority map for sorting
  const severityPriority = { 'critical': 3, 'major': 2, 'minor': 1 };

  // Sort complaints based on current sort settings
  const getSortedComplaints = (complaints) => {
    const sorted = [...complaints].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === 'severity') {
        aValue = severityPriority[a.severity] || 0;
        bValue = severityPriority[b.severity] || 0;
      } else if (sortColumn === 'age') {
        aValue = a.age_hours || 0;
        bValue = b.age_hours || 0;
      } else if (sortColumn === 'complaint_id') {
        aValue = a.complaint_identifier || '';
        bValue = b.complaint_identifier || '';
        if (sortDirection === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      } else if (sortColumn === 'reported_by') {
        aValue = a.reporter_full_name || '';
        bValue = b.reporter_full_name || '';
        if (sortDirection === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }

      // Numeric comparison
      if (sortDirection === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    return sorted;
  };

  // Render sort indicator
  const renderSortIndicator = (column) => {
    if (sortColumn !== column) {
      return <span style={{ marginLeft: '0.5rem', color: '#9ca3af' }}>⇅</span>;
    }
    return sortDirection === 'desc'
      ? <span style={{ marginLeft: '0.5rem', color: 'var(--dp-accent-primary)' }}>↓</span>
      : <span style={{ marginLeft: '0.5rem', color: 'var(--dp-accent-primary)' }}>↑</span>;
  };

  const formatActivityTime = (timestamp) => {
    const d = new Date(timestamp);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <BaseDashboard
      user={user}
      token={token}
    >
      {(apiResponse, refreshDashboard) => {
        // Extract dashboard data from API response
        const dashboardData = apiResponse?.data || {};

        const quick_stats = dashboardData?.quick_stats || {};
        const location_alerts = dashboardData?.location_alerts || [];
        const unassigned_complaints = dashboardData?.unassigned_complaints || [];
        const technician_capacity = dashboardData?.technician_capacity || [];
        const sla_metrics = dashboardData?.sla_metrics || {};
        const device_summary = dashboardData?.device_summary || {};
        const recent_activity = dashboardData?.recent_activity || [];

        return (
          <DashboardLayout columns="1" gap="md">
            {/* Quick Navigation Links */}
            <div
              className={`admin-quick-nav${isCompactQuickNav ? ' admin-quick-nav--compact' : ''}`}
            >
              <div style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.75rem', color: '#6b7280' }}>
                📍 Quick Navigation
              </div>
              <div className="admin-quick-nav-scroll">
                <div
                  className="admin-quick-nav-links"
                  style={{
                    display: 'flex',
                    flexWrap: isCompactQuickNav ? 'nowrap' : 'wrap',
                    gap: '0.5rem'
                  }}
                >
                  {[
                    { label: '📊 Statistics', id: 'quick-statistics' },
                    { label: '🚨 Alerts', id: 'location-alerts' },
                    { label: '📋 Queue', id: 'unassigned-queue' },
                    { label: '👥 Technicians', id: 'technician-capacity' },
                    { label: '📈 SLA', id: 'sla-performance' },
                    { label: '🖥️ Devices', id: 'device-summary' },
                    { label: '📊 Activity', id: 'recent-activity' }
                  ].map((link) => (
                    <a
                      key={link.id}
                      href={`#${link.id}`}
                      className="admin-quick-nav-link"
                      style={{
                        padding: '0.5rem 0.75rem',
                        backgroundColor: '#f3f4f6',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        color: 'var(--dp-accent-primary)',
                        textDecoration: 'none',
                        fontWeight: '500',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        msUserSelect: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'var(--dp-accent-primary)';
                        e.target.style.color = '#fff';
                        e.target.style.borderColor = 'var(--dp-accent-primary)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#f3f4f6';
                        e.target.style.color = 'var(--dp-accent-primary)';
                        e.target.style.borderColor = '#e5e7eb';
                      }}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* ========================================
                SECTION 1: QUICK STATISTICS
                Complaint counts and alert status
                ======================================== */}
            <Section id="quick-statistics" title="📊 Quick Statistics">
              <div className="list-default-window-note" role="note">
                <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
                <span>
                  Complaint cards are the current pipeline (any age) at this location, matching a status filter on the Complaints list.
                  Device cards match the Devices list (excludes drafts and pending approval).
                </span>
              </div>
              <div className="dashboard-stats-summary">
                {[
                  { label: 'Total Devices', value: quick_stats.total_devices || 0, icon: '📱', className: 'total', action: 'all-devices' },
                  { label: 'Operational', value: quick_stats.operational_devices || 0, icon: '✅', className: 'active', action: 'operational-devices' },
                  { label: 'Open Complaints', value: quick_stats.open_complaints || 0, icon: '🔴', className: 'open', action: 'open-complaints' },
                  { label: 'Ack', value: quick_stats.acknowledged_complaints || 0, icon: '🔵', className: 'acknowledged', action: 'acknowledged-complaints', title: 'Click to view acknowledged complaints' },
                  { label: 'In Progress', value: quick_stats.in_progress_complaints || 0, icon: '🟡', className: 'progress', action: 'in-progress-complaints' },
                  { label: 'Escalated to Vendor', value: quick_stats.escalated_to_vendor_complaints || 0, icon: '🚩', className: 'escalated', action: 'escalated-to-vendor-complaints' },
                  { label: 'Unassigned', value: quick_stats.unassigned_complaints || 0, icon: '⚠️', className: 'uncovered', action: 'unassigned-complaints', elevateWhenPositive: true },
                  { label: 'Critical Alerts', value: quick_stats.critical_alerts || 0, icon: '🚨', className: 'uncovered', action: 'critical-alerts', elevateWhenPositive: true },
                ].map((stat) => {
                  const handleStatClick = () => {
                    switch (stat.action) {
                      case 'all-devices':
                        onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({});
                        break;
                      case 'operational-devices':
                        onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({
                          hasNoActiveComplaints: true,
                          hasActiveComplaints: false,
                          deviceStatus: [],
                        });
                        break;
                      case 'open-complaints':
                        onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({
                          statuses: ['open'],
                        });
                        break;
                      case 'acknowledged-complaints':
                        onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({
                          statuses: ['acknowledged'],
                        });
                        break;
                      case 'in-progress-complaints':
                        onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({
                          statuses: ['in_progress'],
                        });
                        break;
                      case 'escalated-to-vendor-complaints':
                        onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({
                          statuses: ['escalated_to_vendor'],
                        });
                        break;
                      case 'unassigned-complaints': {
                        const queueSection = document.getElementById('unassigned-queue');
                        if (queueSection) {
                          queueSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                        break;
                      }
                      case 'critical-alerts': {
                        const alertsSection = document.getElementById('location-alerts');
                        if (alertsSection) {
                          alertsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                        break;
                      }
                      default:
                        break;
                    }
                  };

                  const elevate = stat.elevateWhenPositive && Number(stat.value) > 0;

                  return (
                    <StatsCard
                      key={stat.action}
                      icon={stat.icon}
                      number={stat.value}
                      label={stat.label}
                      className={`${stat.className}${elevate ? ' alert-elevated' : ''}`.trim()}
                      onClick={handleStatClick}
                      title={stat.title || `Click to view ${stat.label.toLowerCase()}`}
                    />
                  );
                })}
              </div>
            </Section>

            {/* ========================================
                SECTION 2: LOCATION ALERTS
                Critical issues requiring immediate action
                ======================================== */}
            <Section id="location-alerts" title={`🚨 Alerts${location_alerts.length > 0 ? ` (${location_alerts.length})` : ''}`}>
              {location_alerts.length > 0 ? (
                <div className="maintenance-alerts-list">
                  {location_alerts.map((alert, idx) => {
                    const isClickable = [
                      'unassigned_queue',
                      'sla_warning_24h',
                      'sla_violation_48h',
                      'pending_24_48h',
                      'critical_complaint',
                      'technician_overload',
                    ].includes(alert.type);
                    const severityClass = alert.severity === 'critical' ? 'critical' : 'warning';

                    const handleAlertClick = () => {
                      if (!isClickable) return;

                      switch (alert.type) {
                        case 'unassigned_queue': {
                          const queueSection = document.getElementById('unassigned-queue');
                          if (queueSection) {
                            queueSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                          break;
                        }
                        case 'technician_overload': {
                          const technicianSection = document.getElementById('technician-capacity');
                          if (technicianSection) {
                            technicianSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                          break;
                        }
                        case 'sla_warning_24h':
                        case 'pending_24_48h':
                          onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({
                            statuses: ['open', 'acknowledged', 'in_progress', 'escalated_to_vendor'],
                            daysOpenFrom: 1,
                            daysOpenTo: 2,
                          });
                          break;
                        case 'sla_violation_48h':
                          onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({
                            statuses: ['open', 'acknowledged', 'in_progress', 'escalated_to_vendor'],
                            daysOpenFrom: 2,
                            daysOpenTo: '',
                          });
                          break;
                        case 'critical_complaint':
                          onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({
                            statuses: ['open', 'acknowledged', 'in_progress', 'escalated_to_vendor'],
                            severities: ['critical'],
                            daysOpenFrom: '',
                            daysOpenTo: '',
                          });
                          break;
                        default:
                          break;
                      }
                    };

                    return (
                      <div
                        key={idx}
                        className={`maintenance-alert-row ${severityClass}${isClickable ? ' clickable' : ''}`}
                        onClick={handleAlertClick}
                        title={isClickable ? (alert.action || 'Click to view') : undefined}
                      >
                        <div className="maintenance-alert-main">
                          <span>{alert.message}</span>
                          {alert.action ? (
                            <>
                              <span className="maintenance-alert-sep">·</span>
                              <span className="maintenance-alert-action">{alert.action}</span>
                            </>
                          ) : null}
                        </div>
                        <div className="maintenance-alert-meta">
                          <span className={`maintenance-alert-count ${severityClass}`}>
                            {alert.count}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <EmptyState
                  section="Alerts"
                  message="No critical alerts. All systems operating normally."
                  icon="🚨"
                />
              )}
            </Section>

            {/* ========================================
                SECTION 3: UNASSIGNED COMPLAINTS QUEUE
                Most important for help desk workflow
                ======================================== */}
            <Section id="unassigned-queue" title={`📋 Unassigned Complaints Queue (${unassigned_complaints.length})`}>
              {unassigned_complaints.length > 0 ? (
                <div className="critical-complaints-table-container">
                  <table className="critical-complaints-table">
                    <thead>
                      <tr>
                        <th className="col-rank">#</th>
                        <th className="col-complaint" style={{ cursor: 'pointer' }} onClick={() => handleSort('complaint_id')}>
                          Complaint {renderSortIndicator('complaint_id')}
                        </th>
                        <th className="col-device">Device</th>
                        <th className="col-department" style={{ cursor: 'pointer' }} onClick={() => handleSort('severity')}>
                          Severity {renderSortIndicator('severity')}
                        </th>
                        <th className="col-reported" style={{ cursor: 'pointer' }} onClick={() => handleSort('age')}>
                          Age {renderSortIndicator('age')}
                        </th>
                        <th className="col-assigned" style={{ cursor: 'pointer' }} onClick={() => handleSort('reported_by')}>
                          Reported By {renderSortIndicator('reported_by')}
                        </th>
                        <th className="col-status">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getSortedComplaints(unassigned_complaints).map((complaint, idx) => {
                        const ageHours = complaint.age_hours;

                        return (
                          <tr
                            key={idx}
                            className="complaint-row"
                          >
                            {/* Rank */}
                            <td className="col-rank">
                              <span className="rank-badge">{idx + 1}</span>
                            </td>

                            {/* Complaint ID + Title */}
                            <td className="col-complaint complaint-stacked-cell">
                              <div className="stacked-content">
                                <ComplaintIDBadge complaintId={complaint.complaint_identifier || 'N/A'} size="small" />
                                <span className="complaint-title">{complaint.title}</span>
                              </div>
                            </td>

                            {/* Device ID + Name */}
                            <td className="col-device device-stacked-cell">
                              <div className="stacked-content">
                                {complaint.device_identifier ? (
                                  <>
                                    <DeviceIDBadge deviceId={complaint.device_identifier} size="small" />
                                    <span className="device-name">{complaint.device_name || 'N/A'}</span>
                                  </>
                                ) : (
                                  <span style={{ color: '#9ca3af' }}>N/A</span>
                                )}
                              </div>
                            </td>

                            {/* Severity */}
                            <td className="col-department">
                              <SeverityBadge severity={complaint.severity} size="small" />
                            </td>

                            {/* Age with color coding */}
                            <td className="col-reported">
                              <span style={{
                                color: ageHours > 48 ? '#dc2626' : ageHours > 24 ? '#d97706' : '#16a34a',
                                fontSize: '0.75rem',
                                fontWeight: '700',
                                whiteSpace: 'nowrap'
                              }}>
                                {complaint.age_display}
                              </span>
                            </td>

                            {/* Reporter Name + Employee ID */}
                            <td className="col-assigned">
                              <div className="stacked-content">
                                <span className="assigned-text">{complaint.reporter_full_name || 'N/A'}</span>
                                {complaint.reporter_employee_id && (
                                  <EmployeeIDBadge employeeId={complaint.reporter_employee_id} size="small" />
                                )}
                              </div>
                            </td>

                            {/* Action — compact, matches table badge density */}
                            <td className="col-status center">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedComplaintForModal(complaint);
                                }}
                                className="btn-action btn-assign"
                                title="Assign this complaint to a technician"
                              >
                                Assign →
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ padding: '3rem 1.5rem', textAlign: 'center', color: '#6b7280' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✅</div>
                  <div style={{ fontSize: '1rem', fontWeight: '500' }}>All complaints have been assigned!</div>
                  <div style={{ fontSize: '0.85rem', marginTop: '0.5rem', color: '#9ca3af' }}>Great work! Your queue is clear.</div>
                </div>
              )}
            </Section>

            {/* ========================================
                SECTION 4: TECHNICIAN COVERAGE & AVAILABILITY
                For assignment decisions
                ======================================== */}
            <Section id="technician-capacity" title="👥 Technician Coverage & Availability">
              {technician_capacity.length > 0 ? (
                <TeamWorkloadTable technicians={technician_capacity} />
              ) : (
                <div style={{ padding: '3rem 1.5rem', textAlign: 'center', color: '#6b7280' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>👥</div>
                  <div style={{ fontSize: '1rem', fontWeight: '500' }}>No technicians available</div>
                  <div style={{ fontSize: '0.85rem', marginTop: '0.5rem', color: '#9ca3af' }}>Unable to display technician coverage at this time.</div>
                </div>
              )}
            </Section>

            {/* ========================================
                SECTION 5: COMPLAINT SLA PERFORMANCE
                KPIs and metrics
                ======================================== */}
            <Section id="sla-performance" title="📈 Complaint SLA Performance">
              {sla_metrics && Object.keys(sla_metrics).length > 0 ? (
                <>
                  <div className="list-default-window-note" role="note">
                    <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
                    <span>
                      Showing open tickets (any age) and closed/cancelled from the{' '}
                      <strong>last {COMPLAINTS_DEFAULT_LOOKBACK_DAYS} days</strong>.
                      Same default as the Complaints list. SLA Met / Breached use that Closed window.
                    </span>
                  </div>
                  <div className="dashboard-stats-summary" style={{ marginBottom: '1.5rem' }}>
                    {[
                      {
                        label: 'Total Complaints',
                        value: sla_metrics.total_complaints || 0,
                        icon: '📊',
                        className: 'total',
                        action: 'all-complaints',
                        title: 'Open tickets (any age) + closed/cancelled in last 30 days. Click to open Complaints.',
                      },
                      {
                        label: 'SLA Met',
                        value: sla_metrics.sla_met || 0,
                        icon: '✅',
                        className: 'closed',
                        title: `Closed within 2 days, last ${COMPLAINTS_DEFAULT_LOOKBACK_DAYS} days.`,
                      },
                      {
                        label: 'SLA Breached',
                        value: sla_metrics.sla_breached || 0,
                        icon: '❌',
                        className: 'uncovered',
                        elevateWhenPositive: true,
                        title: `Closed after 2 days, last ${COMPLAINTS_DEFAULT_LOOKBACK_DAYS} days.`,
                      },
                      { label: 'Pending >24h', value: sla_metrics.pending_24h || 0, icon: '⏰', className: 'acknowledged', action: 'pending_24h', elevateWhenPositive: true },
                      { label: 'Pending >48h', value: sla_metrics.pending_48h || 0, icon: '🚨', className: 'uncovered', action: 'pending_48h', elevateWhenPositive: true },
                      { label: 'Avg Resolution', value: `${(sla_metrics.avg_resolution_hours || 0).toFixed(1)}h`, icon: '⏱️', className: 'pending' },
                    ].map((metric) => {
                      const handleMetricClick = () => {
                        if (!metric.action || !onNavigateToComplaintsWithFilter) return;

                        switch (metric.action) {
                          case 'all-complaints':
                            onNavigateToComplaintsWithFilter({});
                            break;
                          case 'pending_24h':
                            onNavigateToComplaintsWithFilter({
                              statuses: ['open', 'acknowledged', 'in_progress', 'escalated_to_vendor'],
                              daysOpenFrom: 1,
                              daysOpenTo: '',
                            });
                            break;
                          case 'pending_48h':
                            onNavigateToComplaintsWithFilter({
                              statuses: ['open', 'acknowledged', 'in_progress', 'escalated_to_vendor'],
                              daysOpenFrom: 2,
                              daysOpenTo: '',
                            });
                            break;
                          default:
                            break;
                        }
                      };

                      const elevate = metric.elevateWhenPositive && Number(metric.value) > 0;

                      return (
                        <StatsCard
                          key={metric.label}
                          icon={metric.icon}
                          number={metric.value}
                          label={metric.label}
                          className={`${metric.className}${elevate ? ' alert-elevated' : ''}`.trim()}
                          onClick={metric.action ? handleMetricClick : undefined}
                          title={metric.title || (metric.action ? `Click to view ${metric.label.toLowerCase()}` : undefined)}
                        />
                      );
                    })}
                  </div>

                  <div className="dashboard-subsection">
                    <h3 className="dashboard-subsection-title">By Status</h3>
                    <div className="dashboard-stats-summary">
                      {[
                        { status: 'open', label: 'Open', value: sla_metrics.by_status?.open || 0, icon: '🔴', className: 'open' },
                        { status: 'acknowledged', label: 'Ack', value: sla_metrics.by_status?.acknowledged || 0, icon: '🔵', className: 'acknowledged', title: 'Click to view acknowledged complaints' },
                        { status: 'in_progress', label: 'In Progress', value: sla_metrics.by_status?.in_progress || 0, icon: '🟡', className: 'progress' },
                        { status: 'escalated_to_vendor', label: 'Escalated', value: sla_metrics.by_status?.escalated_to_vendor || 0, icon: '🚩', className: 'escalated' },
                        {
                          status: 'closed',
                          label: 'Closed',
                          value: sla_metrics.by_status?.closed || 0,
                          icon: '✅',
                          className: 'closed',
                          title: `Closed in last ${COMPLAINTS_DEFAULT_LOOKBACK_DAYS} days (Complaints list default). Click to view.`,
                        },
                        {
                          status: 'cancelled',
                          label: 'Cancelled',
                          value: sla_metrics.by_status?.cancelled || 0,
                          icon: '❌',
                          className: 'cancelled',
                          title: `Cancelled in last ${COMPLAINTS_DEFAULT_LOOKBACK_DAYS} days (Complaints list default). Click to view.`,
                        },
                      ].map((item) => (
                        <StatsCard
                          key={item.status}
                          icon={item.icon}
                          number={item.value}
                          label={item.label}
                          className={item.className}
                          onClick={() => onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({
                            statuses: [item.status],
                          })}
                          title={item.title || `Click to view ${item.label.toLowerCase()} complaints`}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="dashboard-subsection">
                    <h3 className="dashboard-subsection-title">Complaint Metrics</h3>
                    <ComplaintPerformanceMetricsCards metrics={sla_metrics} />
                  </div>

                  <div className="dashboard-subsection">
                    <h3 className="dashboard-subsection-title">Severity Breakdown</h3>
                    <div className="severity-breakdown-groups">
                      <div className="severity-breakdown-group">
                        <div className="dashboard-subsection-label">
                          Active ({(sla_metrics.by_severity_active?.critical || 0) + (sla_metrics.by_severity_active?.major || 0) + (sla_metrics.by_severity_active?.minor || 0)})
                        </div>
                        <div className="dashboard-stats-summary">
                          {[
                            { severity: 'critical', label: 'Critical', className: 'uncovered', value: sla_metrics.by_severity_active?.critical || 0, icon: '🔴' },
                            { severity: 'major', label: 'Major', className: 'acknowledged', value: sla_metrics.by_severity_active?.major || 0, icon: '🟠' },
                            { severity: 'minor', label: 'Minor', className: 'total', value: sla_metrics.by_severity_active?.minor || 0, icon: '🟢' },
                          ].map((item) => (
                            <StatsCard
                              key={`active-${item.severity}`}
                              icon={item.icon}
                              number={item.value}
                              label={item.label}
                              className={`${item.className}${Number(item.value) > 0 && item.severity === 'critical' ? ' alert-elevated' : ''}`}
                              onClick={() => onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({
                                statuses: ['open', 'acknowledged', 'in_progress', 'escalated_to_vendor'],
                                severities: [item.severity],
                              })}
                              title={`Click to view ${item.label.toLowerCase()} active complaints`}
                            />
                          ))}
                        </div>
                      </div>
                      <div className="severity-breakdown-group">
                        <div className="dashboard-subsection-label">
                          Closed / Cancelled ({(sla_metrics.by_severity_closed?.critical || 0) + (sla_metrics.by_severity_closed?.major || 0) + (sla_metrics.by_severity_closed?.minor || 0)})
                        </div>
                        <div className="dashboard-stats-summary">
                          {[
                            { severity: 'critical', label: 'Critical', className: 'uncovered', value: sla_metrics.by_severity_closed?.critical || 0, icon: '🔴' },
                            { severity: 'major', label: 'Major', className: 'acknowledged', value: sla_metrics.by_severity_closed?.major || 0, icon: '🟠' },
                            { severity: 'minor', label: 'Minor', className: 'total', value: sla_metrics.by_severity_closed?.minor || 0, icon: '🟢' },
                          ].map((item) => (
                            <StatsCard
                              key={`closed-${item.severity}`}
                              icon={item.icon}
                              number={item.value}
                              label={item.label}
                              className={item.className}
                              onClick={() => onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({
                                statuses: ['closed', 'cancelled'],
                                severities: [item.severity],
                              })}
                              title={`Click to view ${item.label.toLowerCase()} closed/cancelled (last ${COMPLAINTS_DEFAULT_LOOKBACK_DAYS} days)`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <EmptyState
                  section="Complaint SLA Performance"
                  message="No SLA data available. Metrics will appear once complaints are tracked."
                  icon="📈"
                />
              )}
            </Section>

            {/* ========================================
                SECTION 6: DEVICE SUMMARY
                Device status and maintenance tracking
                ======================================== */}
            <Section id="device-summary" title="🖥️ Device Summary">
              {device_summary && Object.keys(device_summary).length > 0 ? (
                <>
                  <div className="list-default-window-note" role="note">
                    <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
                    <span>
                      Current inventory snapshot matching the Devices list (excludes drafts and pending approval).
                      Click a card to open the matching list.
                    </span>
                  </div>
                  <div className="dashboard-stats-summary">
                    {[
                      {
                        label: 'Total Devices',
                        value: device_summary.total_devices || 0,
                        icon: '📱',
                        className: 'total',
                        filter: {},
                        title: 'Click to view all devices',
                      },
                      {
                        label: 'Operational',
                        value: device_summary.operational_devices || 0,
                        icon: '✅',
                        className: 'active',
                        filter: { hasNoActiveComplaints: true, hasActiveComplaints: false, deviceStatus: [] },
                        title: 'Devices with no active complaints. Click to view.',
                      },
                      {
                        label: 'With Complaints',
                        value: device_summary.devices_in_complaints || 0,
                        icon: '⚠️',
                        className: 'open',
                        filter: { hasActiveComplaints: true, hasNoActiveComplaints: false },
                        title: 'Devices with an open complaint. Click to view.',
                      },
                      {
                        label: 'Maint. Overdue',
                        value: device_summary.maintenance_overdue || 0,
                        icon: '🔴',
                        className: 'uncovered',
                        elevateWhenPositive: true,
                        filter: {
                          ppmStatus: ['Overdue (0-30d)', 'Overdue (30+d)'],
                          calibrationStatus: ['Overdue (0-30d)', 'Overdue (30+d)'],
                          complianceStatus: ['Overdue (0-30d)', 'Overdue (30+d)'],
                        },
                        title: 'Overdue PPM, calibration, or compliance. Click to view.',
                      },
                      {
                        label: 'Due in 7 days',
                        value: device_summary.maintenance_due_week || 0,
                        icon: '📅',
                        className: 'calibration',
                        title: 'PPM, calibration, or compliance due in the next 7 days.',
                      },
                      {
                        label: 'Due in 30 days',
                        value: device_summary.maintenance_due_month || 0,
                        icon: '📆',
                        className: 'pending',
                        filter: {
                          ppmStatus: ['Due - Expiring Soon (≤30d)'],
                          calibrationStatus: ['Due - Expiring Soon (≤30d)'],
                          complianceStatus: ['Due - Expiring Soon (≤30d)'],
                        },
                        title: 'PPM, calibration, or compliance due within 30 days. Click to view.',
                      },
                    ].map((stat) => {
                      const elevate = stat.elevateWhenPositive && Number(stat.value) > 0;
                      return (
                        <StatsCard
                          key={stat.label}
                          icon={stat.icon}
                          number={stat.value}
                          label={stat.label}
                          className={`${stat.className}${elevate ? ' alert-elevated' : ''}`.trim()}
                          onClick={stat.filter && onNavigateToDevicesWithFilter
                            ? () => onNavigateToDevicesWithFilter(stat.filter)
                            : undefined}
                          title={stat.title}
                        />
                      );
                    })}
                  </div>
                </>
              ) : (
                <EmptyState
                  section="Device Summary"
                  message="No device data available. Summary will appear once devices are added."
                  icon="🖥️"
                />
              )}
            </Section>

            {/* ========================================
                SECTION 7: RECENT ACTIVITY
                Workflow audit trail
                ======================================== */}
            <Section id="recent-activity" title={`📊 Recent Activity${recent_activity.length > 0 ? ' (Last 7 Days)' : ''}`}>
              {recent_activity.length > 0 ? (
                (() => {
                  const today = new Date();
                  const sevenDaysAgo = new Date(today);
                  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                  const formatDate = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  const dateRange = `${formatDate(sevenDaysAgo)} - ${formatDate(today)}`;

                  const stats = {
                    opened: 0,
                    assigned: 0,
                    reassigned: 0,
                    escalated: 0,
                    closed: 0,
                    cancelled: 0,
                  };

                  recent_activity.forEach((act) => {
                    const newValue = act.details?.new_value;
                    if (act.activity_type === 'creation') stats.opened += 1;
                    else if (act.activity_type === 'assignment') stats.assigned += 1;
                    else if (act.activity_type === 'reassignment') stats.reassigned += 1;
                    else if (act.activity_type === 'escalation') stats.escalated += 1;
                    else if (act.activity_type === 'complaint_closed' || (act.activity_type === 'status_change' && newValue === 'closed')) stats.closed += 1;
                    else if (act.activity_type === 'complaint_cancelled' || (act.activity_type === 'status_change' && newValue === 'cancelled')) stats.cancelled += 1;
                  });

                  const activityDescription = (act) => {
                    if (act.activity_type === 'creation') return 'Opened';
                    return act.description || act.activity_type;
                  };

                  return (
                    <div>
                      <div className="list-default-window-note" role="note">
                        <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
                        <span>Complaint workflow log for this location, <strong>{dateRange}</strong>.</span>
                      </div>
                      <div className="dashboard-stats-summary" style={{ marginBottom: '1rem' }}>
                        <StatsCard icon="📥" number={stats.opened} label="Opened" className="open" />
                        <StatsCard icon="👤" number={stats.assigned} label="Assigned" className="progress" />
                        <StatsCard icon="🔄" number={stats.reassigned} label="Reassigned" className="pending" />
                        <StatsCard icon="🚩" number={stats.escalated} label="Escalated" className="escalated" />
                        <StatsCard icon="✅" number={stats.closed} label="Closed" className="closed" />
                        <StatsCard icon="❌" number={stats.cancelled} label="Cancelled" className="cancelled" />
                      </div>

                      <div className="dashboard-activity-list">
                        {recent_activity.slice(0, 20).map((act, idx) => (
                          <div
                            key={idx}
                            className="dashboard-activity-row is-complaint"
                          >
                            <div className="dashboard-activity-row__main">
                              {act.complaint_identifier && (
                                <ComplaintIDBadge complaintId={act.complaint_identifier} size="small" />
                              )}
                              <span className="dashboard-activity-row__desc">{activityDescription(act)}</span>
                            </div>
                            <div className="dashboard-activity-row__meta">
                              <span title={act.actor_employee_id || undefined}>{act.actor_name || 'Unknown'}</span>
                              <span>•</span>
                              <span>{formatActivityTime(act.timestamp)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()
              ) : (
                <EmptyState
                  section="Recent Activity"
                  message="No complaint workflow activity in the last 7 days."
                  icon="📊"
                />
              )}
            </Section>

            {/* ========================================
                COMPLAINT VIEW MODAL
                Opens when user clicks on a complaint or Assign button
                ======================================== */}
            {selectedComplaintForModal && (
              <ComplaintView
                complaintId={selectedComplaintForModal.id}
                token={token}
                onClose={() => setSelectedComplaintForModal(null)}
                onUpdate={() => handleComplaintUpdate(refreshDashboard)}
                isFullPage={false}
                user={user}
              />
            )}
          </DashboardLayout>
        );
      }}
    </BaseDashboard>
  );
};

export default CentralHelpDeskDashboard;